import { Loader2, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Navigate } from "react-router-dom";

import { Brand } from "@/components/Brand.tsx";
import { ApiError, api } from "@/lib/api.ts";
import type { LoginOut, TotpSetupOut } from "@/lib/types.ts";
import { useSession } from "@/stores/session.ts";

const input =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15";

export function LoginPage() {
  const { token, requires2faSetup } = useSession();
  if (token && !requires2faSetup) return <Navigate to="/" replace />;
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-[1.375rem] border border-slate-200 bg-white p-7">
        <div className="mb-6">
          <Brand caption="Consola de plataforma" />
        </div>
        {token && requires2faSetup ? <TotpEnrollment /> : <PasswordStep />}
      </div>
    </div>
  );
}

function PasswordStep() {
  const setSession = useSession((s) => s.set);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const out = await api.post<LoginOut>("/auth/login", {
        email,
        password,
        totp_code: code || null,
      });
      setSession(out.access_token, out.requires_2fa_setup);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "No se pudo conectar";
      // Credenciales correctas pero falta el código: se pide sin borrar la clave.
      if (message.includes("2FA") && !code) setNeedsCode(true);
      else setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        autoComplete="username"
        required
        className={input}
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Contraseña"
        autoComplete="current-password"
        required
        className={input}
      />
      {needsCode && (
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Código de la app autenticadora"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          className={input}
        />
      )}
      {error && <p className="text-sm text-red-700">{error}</p>}
      <button
        type="submit"
        disabled={busy}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Entrar
      </button>
    </form>
  );
}

function TotpEnrollment() {
  const { set: setSession, clear } = useSession();
  const [setup, setSetup] = useState<TotpSetupOut | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setSetup(await api.post<TotpSetupOut>("/auth/totp/setup"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo iniciar");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setup) return;
    setBusy(true);
    setError(null);
    try {
      const out = await api.post<LoginOut>("/auth/totp/confirm", { secret: setup.secret, code });
      setSession(out.access_token, out.requires_2fa_setup);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo confirmar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
        <ShieldCheck className="h-5 w-5 shrink-0" />
        <p>
          Esta consola ve todas las empresas: la verificación en dos pasos es obligatoria.
          Configúrala con Google Authenticator, Microsoft Authenticator o similar.
        </p>
      </div>
      {!setup ? (
        <button
          type="button"
          onClick={start}
          disabled={busy}
          className="w-full rounded-lg bg-brand-600 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
        >
          Configurar ahora
        </button>
      ) : (
        <form onSubmit={confirm} className="space-y-3">
          <img src={setup.qr_image} alt="QR para la app autenticadora" className="mx-auto h-44 w-44" />
          <p className="text-center text-xs break-all text-slate-500">
            ¿No puedes escanear? Clave: <span className="font-mono">{setup.secret}</span>
          </p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Código de 6 dígitos"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            className={input}
          />
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-brand-600 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            Confirmar
          </button>
        </form>
      )}
      {error && <p className="text-sm text-red-700">{error}</p>}
      <button
        type="button"
        onClick={clear}
        className="w-full text-center text-xs text-slate-500 hover:text-slate-800"
      >
        Salir
      </button>
    </div>
  );
}
